import { useEffect, useMemo, useState } from "react";
import { apiGet } from "@/lib/api";

const DEFAULT_SYMBOLS = [
  "XAUUSD",
  "BTCUSD",
  "EURUSD",
  "GBPUSD",
  "USDJPY",
];

function fmt(v) {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "number") {
    return Number.isInteger(v) ? String(v) : v.toFixed(2);
  }
  return String(v);
}

function Section({ title, subtitle, children }) {
  return (
    <section className="rounded-2xl border border-zinc-800 bg-zinc-950/80 p-4 shadow-lg shadow-black/20">
      <div className="mb-4">
        <div className="text-xs font-bold uppercase tracking-[0.18em] text-zinc-300">
          {title}
        </div>
        {subtitle && (
          <div className="mt-1 text-[11px] text-zinc-600">
            {subtitle}
          </div>
        )}
      </div>
      {children}
    </section>
  );
}

function StatusBadge({ status }) {
  const s = String(status || "WAIT").toUpperCase();

  let cls = "border-zinc-700 bg-zinc-900 text-zinc-400";

  if (["SIGNAL", "SIGNAL_READY", "OPEN"].includes(s)) {
    cls = "border-emerald-700 bg-emerald-950/60 text-emerald-400";
  } else if (["PENDING", "PENDING_ENTRY", "WAIT_CONFIRMATION", "SETUP_FORMING"].includes(s)) {
    cls = "border-amber-700 bg-amber-950/50 text-amber-400";
  } else if (["INVALID", "LOSS"].includes(s)) {
    cls = "border-red-800 bg-red-950/50 text-red-400";
  } else if (["CLOSED", "TP1_HIT", "WIN"].includes(s)) {
    cls = "border-cyan-800 bg-cyan-950/50 text-cyan-400";
  }

  return (
    <span className={`inline-flex rounded-full border px-3 py-1 text-[11px] font-bold tracking-wide ${cls}`}>
      {s}
    </span>
  );
}

function DirectionBadge({ direction }) {
  const d = String(direction || "WAIT").toUpperCase();
  const sell = d === "SELL";
  const buy = d === "BUY";

  return (
    <span
      className={`inline-flex rounded-lg border px-3 py-1 text-xs font-bold ${
        sell
          ? "border-red-800 bg-red-950/50 text-red-400"
          : buy
          ? "border-emerald-800 bg-emerald-950/50 text-emerald-400"
          : "border-zinc-700 bg-zinc-900 text-zinc-400"
      }`}
    >
      {d}
    </span>
  );
}

function Check({ value }) {
  const yes =
    value === true ||
    value === "done" ||
    value === "bullish" ||
    value === "bearish";

  return (
    <span className={yes ? "text-emerald-400" : "text-zinc-600"}>
      {yes ? "✓" : "—"}
    </span>
  );
}

function ReasonRow({ children }) {
  return (
    <div className="flex items-start gap-2 text-xs text-zinc-400">
      <span className="mt-0.5 text-emerald-400">✓</span>
      <span>{children}</span>
    </div>
  );
}

function Metric({ label, value, emphasis }) {
  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-900/70 p-3">
      <div className="text-[10px] uppercase tracking-wider text-zinc-600">
        {label}
      </div>
      <div
        className={`mt-1 text-sm font-bold ${
          emphasis || "text-white"
        }`}
      >
        {fmt(value)}
      </div>
    </div>
  );
}

function TimeframeCard({ name, title, children }) {
  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-900/60 p-4">
      <div className="mb-3 flex items-center justify-between">
        <div>
          <div className="text-xs font-bold tracking-widest text-white">
            {name}
          </div>
          <div className="text-[10px] text-zinc-600">{title}</div>
        </div>
      </div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function TFRow({ label, value }) {
  return (
    <div className="flex items-center justify-between border-b border-zinc-800/70 py-1.5 last:border-0">
      <span className="text-[11px] text-zinc-500">{label}</span>
      <span className="flex items-center gap-1.5 text-[11px] text-zinc-300">
        <Check value={value} />
        {typeof value === "boolean" ? "" : fmt(value)}
      </span>
    </div>
  );
}

export function LiquidityFlow() {
  const [symbol, setSymbol] = useState("XAUUSD");
  const [data, setData] = useState(null);
  const [history, setHistory] = useState([]);
  const [performance, setPerformance] = useState(null);
  const [paper, setPaper] = useState(null);
  const [error, setError] = useState("");

  const load = async () => {
    try {
      setError("");

      const [flowRes, histRes, perfRes, pnlRes] = await Promise.all([
        apiGet(`/liquidity-flow/${symbol}`),
        apiGet(`/liquidity-flow/${symbol}/history/list`),
        apiGet(`/liquidity-flow/${symbol}/performance`),
        apiGet(`/liquidity-flow/${symbol}/paper-pnl`),
      ]);

      const flow = flowRes?.data ?? flowRes;
      const hist = histRes?.data ?? histRes;
      const perf = perfRes?.data ?? perfRes;
      const pnl = pnlRes?.data ?? pnlRes;

      setData(flow);
      setHistory(Array.isArray(hist) ? hist : []);
      setPerformance(perf);
      setPaper(pnl);
    } catch (e) {
      setError(e?.message || "Liquidity Flow error");
    }
  };

  useEffect(() => {
    load();
    const timer = setInterval(load, 10000);
    return () => clearInterval(timer);
  }, [symbol]);

  const active = data?.active_signal;
  const h1 = data?.timeframes?.H1 || {};
  const m30 = data?.timeframes?.M30 || {};
  const m15 = data?.timeframes?.M15 || {};
  const m5 = data?.timeframes?.M5 || {};

  const symbols = useMemo(() => {
    const available = data?.available_symbols || [];
    return [...new Set([...DEFAULT_SYMBOLS, ...available])];
  }, [data]);

  const status =
    active?.status ||
    data?.status ||
    "WAIT";

  const direction =
    active?.direction ||
    data?.direction ||
    "WAIT";

  const entryLow = active?.entry_low ?? data?.entry_low;
  const entryHigh = active?.entry_high ?? data?.entry_high;
  const entry = active?.entry ?? data?.entry;
  const sl = active?.sl ?? data?.sl;
  const tp1 = active?.tp1 ?? data?.tp1;
  const tp2 = active?.tp2 ?? data?.tp2;
  const tp3 = active?.tp3 ?? data?.tp3;
  const tp4 = active?.tp4 ?? data?.tp4;
  const rr = active?.rr ?? data?.rr;

  const zoneReason = data?.zone_reason || {};
  const reasonItems = Array.isArray(zoneReason)
    ? zoneReason
    : Object.values(zoneReason);

  const score = active?.score ?? data?.score;
  const zoneQuality =
    active?.zone_quality ?? data?.zone_quality;

  const confidence =
    active?.confidence_category ||
    data?.confidence_category ||
    (score >= 90
      ? "VERY HIGH CONFIDENCE"
      : score >= 80
      ? "HIGH CONFIDENCE"
      : score >= 70
      ? "VALID"
      : "WAIT");

  const pending =
    status === "PENDING" ||
    status === "PENDING_ENTRY" ||
    status === "WAIT_CONFIRMATION";

  const m5Ready =
    m5?.liquidity_sweep &&
    m5?.rejection &&
    m5?.structure_shift;

  return (
    <div className="space-y-4 pb-6">

      {/* HEADER */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-xl font-black tracking-tight text-white">
            LIQUIDITY FLOW
          </div>
          <div className="mt-1 text-[11px] uppercase tracking-[0.18em] text-zinc-600">
            Market Reasoning Engine
          </div>
        </div>

        <div className="flex items-center gap-2">
          <select
            value={symbol}
            onChange={(e) => setSymbol(e.target.value)}
            className="rounded-xl border border-zinc-700 bg-zinc-900 px-3 py-2 text-xs font-semibold text-white outline-none"
          >
            {symbols.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>

          <span className="rounded-full border border-emerald-800 bg-emerald-950/60 px-3 py-1.5 text-[10px] font-bold tracking-wider text-emerald-400">
            ● LIVE
          </span>
        </div>
      </div>

      {error && (
        <div className="rounded-xl border border-red-900 bg-red-950/40 p-3 text-xs text-red-400">
          {error}
        </div>
      )}

      {/* MARKET STATE */}
      <Section
        title="MARKET STATE"
        subtitle="Context first — entry comes last"
      >
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="mb-2 flex items-center gap-2">
              <DirectionBadge direction={direction} />
              <StatusBadge status={status} />
            </div>

            <div className="text-xs text-zinc-500">
              {confidence}
            </div>
          </div>

          <div className="text-right">
            <div className="text-3xl font-black text-white">
              {fmt(score)}
              <span className="text-sm text-zinc-600">/100</span>
            </div>
            <div className="text-[10px] uppercase tracking-wider text-zinc-600">
              Confidence Score
            </div>
          </div>
        </div>
      </Section>

      {/* PENDING SETUP */}
      <div className="rounded-2xl border border-emerald-900/70 bg-gradient-to-br from-zinc-950 to-emerald-950/20 p-4 shadow-xl shadow-emerald-950/10">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-emerald-500">
              {pending ? "PENDING SETUP" : "TRADE SETUP"}
            </div>
            <div className="mt-1 text-lg font-black text-white">
              {direction}{" "}
              {pending ? "— WAITING FOR PRICE" : ""}
            </div>
          </div>

          <StatusBadge status={status} />
        </div>

        <div className="mt-4 grid grid-cols-2 gap-2 md:grid-cols-5">
          <Metric
            label="Entry Zone Low"
            value={entryLow}
            emphasis="text-amber-400"
          />
          <Metric
            label="Entry Zone High"
            value={entryHigh}
            emphasis="text-amber-400"
          />
          <Metric label="Entry" value={entry} />
          <Metric
            label="SL"
            value={sl}
            emphasis="text-red-400"
          />
          <Metric
            label="RR"
            value={rr}
            emphasis="text-emerald-400"
          />
        </div>

        <div className="mt-2 grid grid-cols-2 gap-2">
          <Metric
            label="TP1 — Opposing Liquidity"
            value={tp1}
            emphasis="text-cyan-400"
          />
          <Metric
            label="TP2 — Major Liquidity"
            value={tp2}
            emphasis="text-cyan-400"
          />
          <Metric
            label="TP3 — Liquidity"
            value={tp3}
            emphasis="text-cyan-400"
          />
          <Metric
            label="TP4 — Liquidity"
            value={tp4}
            emphasis="text-cyan-400"
          />
        </div>

        {zoneQuality !== undefined && (
          <div className="mt-3 flex items-center justify-between rounded-xl border border-zinc-800 bg-black/20 px-3 py-2">
            <span className="text-[10px] uppercase tracking-wider text-zinc-600">
              Zone Quality
            </span>
            <span className="font-bold text-amber-400">
              {fmt(zoneQuality)}/100
            </span>
          </div>
        )}
      </div>

      {/* WHY THIS ZONE */}
      <Section
        title="WHY THIS ZONE?"
        subtitle="Zone selection comes from multi-timeframe confluence"
      >
        <div className="space-y-2">
          {reasonItems.length ? (
            reasonItems.map((reason, i) => (
              <ReasonRow key={i}>
                {typeof reason === "string"
                  ? reason
                  : JSON.stringify(reason)}
              </ReasonRow>
            ))
          ) : (
            <>
              <ReasonRow>H1 context / liquidity / POI</ReasonRow>
              <ReasonRow>M30 structure refinement</ReasonRow>
              <ReasonRow>M15 break + retest</ReasonRow>
              <div className="text-xs text-zinc-600">
                Detailed zone reasoning belum tersedia dari engine.
              </div>
            </>
          )}
        </div>
      </Section>

      {/* TIMEFRAME REASONING */}
      <Section
        title="MARKET REASONING"
        subtitle="H1 → M30 → M15 → M5"
      >
        <div className="grid gap-3 md:grid-cols-2">

          <TimeframeCard name="H1" title="CONTEXT / LIQUIDITY / POI">
            <TFRow label="Direction" value={h1.direction} />
            <TFRow label="Liquidity" value={h1.liquidity_detected} />
            <TFRow label="POI" value={h1.poi_detected} />
            <TFRow label="POI 50%" value={h1.poi_50} />
            <TFRow label="Freshness" value={h1.fresh} />
          </TimeframeCard>

          <TimeframeCard name="M30" title="SETUP REFINEMENT">
            <TFRow label="Structure" value={m30.structure} />
            <TFRow label="BOS" value={m30.bos} />
            <TFRow label="Breakout" value={m30.breakout} />
            <TFRow label="SBR" value={m30.sbr} />
            <TFRow label="RBS" value={m30.rbs} />
            <TFRow label="MTF Alignment" value={m30.mtf_alignment} />
          </TimeframeCard>

          <TimeframeCard name="M15" title="SETUP CONFIRMATION">
            <TFRow label="Displacement" value={m15.displacement} />
            <TFRow label="Break" value={m15.break} />
            <TFRow label="Retest" value={m15.retest} />
            <TFRow label="Structure" value={m15.structure} />
          </TimeframeCard>

          <TimeframeCard name="M5" title="EXECUTION / TRIGGER">
            <TFRow label="Liquidity Sweep" value={m5.liquidity_sweep} />
            <TFRow label="Rejection" value={m5.rejection} />
            <TFRow label="Structure Shift" value={m5.structure_shift} />
            <TFRow label="Entry Refinement" value={m5.entry_refinement} />
          </TimeframeCard>

        </div>
      </Section>

      {/* WHAT ARE WE WAITING FOR */}
      <Section
        title="WHAT ARE WE WAITING FOR?"
        subtitle="The engine does not chase price"
      >
        <div className="rounded-xl border border-amber-900/60 bg-amber-950/20 p-4">
          {m5Ready ? (
            <div className="space-y-2">
              <div className="text-sm font-bold text-emerald-400">
                ✓ M5 trigger sequence complete
              </div>
              <div className="text-xs text-zinc-400">
                Sweep → Rejection → Structure Shift
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <div className="text-sm font-bold text-amber-400">
                WAIT — M5 confirmation belum lengkap
              </div>

              <div className="grid gap-2 text-xs sm:grid-cols-3">
                <div className="rounded-lg bg-black/20 p-2">
                  <Check value={m5.liquidity_sweep} />{" "}
                  Liquidity Sweep
                </div>
                <div className="rounded-lg bg-black/20 p-2">
                  <Check value={m5.rejection} />{" "}
                  Rejection
                </div>
                <div className="rounded-lg bg-black/20 p-2">
                  <Check value={m5.structure_shift} />{" "}
                  Structure Shift
                </div>
              </div>

              <div className="pt-1 text-[11px] text-zinc-600">
                Tidak ada chase. Tunggu sequence konfirmasi sesuai
                Liquidity Flow.
              </div>
            </div>
          )}
        </div>
      </Section>

      {/* LIFECYCLE */}
      <Section
        title="TRADE LIFECYCLE"
        subtitle="State berasal dari backend lifecycle engine"
      >
        <div className="overflow-x-auto">
          <div className="flex min-w-[650px] items-center gap-2">
            {[
              "PENDING_ENTRY",
              "OPEN",
              "TP1_HIT",
              "CLOSED",
            ].map((x, i) => (
              <div key={x} className="flex items-center gap-2">
                <div
                  className={`rounded-lg border px-3 py-2 text-[10px] font-bold ${
                    status === x
                      ? "border-emerald-700 bg-emerald-950/50 text-emerald-400"
                      : "border-zinc-800 bg-zinc-900 text-zinc-600"
                  }`}
                >
                  {x}
                </div>
                {i < 3 && (
                  <span className="text-zinc-700">→</span>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="mt-3 text-[11px] text-zinc-600">
          Alternative exit:{" "}
          <span className="text-red-400">
            SL / INVALID
          </span>
        </div>
      </Section>

      {/* HISTORY */}
      <Section
        title="SIGNAL HISTORY"
        subtitle="Lifecycle history dari backend"
      >
        <div className="overflow-x-auto">
          <table className="w-full min-w-[800px] text-left text-xs">
            <thead className="text-zinc-600">
              <tr>
                <th className="p-2">ID</th>
                <th className="p-2">Direction</th>
                <th className="p-2">Entry</th>
                <th className="p-2">SL</th>
                <th className="p-2">TP1</th>
                <th className="p-2">TP2</th>
                <th className="p-2">Score</th>
                <th className="p-2">Status</th>
                <th className="p-2">Result</th>
              </tr>
            </thead>

            <tbody>
              {history.map((x) => (
                <tr
                  key={x.signal_id}
                  className="border-t border-zinc-900"
                >
                  <td className="p-2 text-zinc-500">
                    {x.signal_id}
                  </td>
                  <td className="p-2">
                    <DirectionBadge direction={x.direction} />
                  </td>
                  <td className="p-2">{fmt(x.entry)}</td>
                  <td className="p-2 text-red-400">{fmt(x.sl)}</td>
                  <td className="p-2 text-cyan-400">{fmt(x.tp1)}</td>
                  <td className="p-2 text-cyan-400">{fmt(x.tp2)}</td>
                  <td className="p-2">{fmt(x.score)}</td>
                  <td className="p-2">
                    <StatusBadge status={x.status} />
                  </td>
                  <td className="p-2 text-zinc-400">
                    {x.result || "—"}
                  </td>
                </tr>
              ))}

              {!history.length && (
                <tr>
                  <td
                    colSpan="9"
                    className="p-5 text-center text-zinc-600"
                  >
                    Belum ada signal history.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Section>

      {/* PERFORMANCE */}
      <div className="grid gap-4 md:grid-cols-2">

        <Section
          title="PERFORMANCE"
          subtitle="Actual lifecycle statistics"
        >
          {performance && (
            <div className="grid grid-cols-2 gap-2">
              <Metric label="Signals" value={performance.signals} />
              <Metric label="Triggered" value={performance.triggered} />
              <Metric label="WIN" value={performance.wins} emphasis="text-emerald-400" />
              <Metric label="LOSS" value={performance.losses} emphasis="text-red-400" />
              <Metric label="Invalid / Expired" value={performance.expired} />
              <Metric label="Win Rate" value={`${fmt(performance.win_rate)}%`} emphasis="text-emerald-400" />
              <Metric label="Total Pips" value={performance.total_pips} />
              <Metric label="Average Pips" value={performance.average_pips} />
            </div>
          )}
        </Section>

        <Section
          title="PAPER PNL"
          subtitle="Simulation result"
        >
          {paper && (
            <div className="grid grid-cols-2 gap-2">
              <Metric
                label="Starting Balance"
                value={paper.starting_balance}
              />
              <Metric
                label="Current Balance"
                value={paper.balance}
              />
              <Metric label="Lot Size" value={paper.lot_size} />
              <Metric
                label="Risk / Trade"
                value={paper.risk_per_trade}
              />

              <div className="col-span-2 rounded-xl border border-emerald-900/60 bg-emerald-950/20 p-4">
                <div className="text-[10px] uppercase tracking-wider text-zinc-600">PAPER PNL</div>
                <div className="mt-1 text-2xl font-black text-emerald-400">
                  {fmt(paper.pnl_pips ?? paper.pnl)} PIPS
                </div>
                <div className="mt-4">
                  <div className="mb-2 flex items-center justify-between">
                    <span className="text-[10px] uppercase tracking-wider text-zinc-600">CUMULATIVE PIPS</span>
                    <span className="text-xs font-bold text-white">
                      {fmt(paper.equity_curve?.length ? paper.equity_curve[paper.equity_curve.length - 1].cumulative_pips : 0)} PIPS
                    </span>
                  </div>
                  <div className="h-40 w-full overflow-hidden rounded-lg border border-zinc-800 bg-zinc-950 p-2">
                    {paper.equity_curve?.length > 0 ? (
                      <svg viewBox="0 0 100 40" preserveAspectRatio="none" className="h-full w-full">
                        {(() => {
                          const values = paper.equity_curve.map(x => Number(x.cumulative_pips) || 0);
                          const min = Math.min(...values, 0);
                          const max = Math.max(...values, 0);
                          const range = max - min || 1;
                          const coords = values.map((value, index) => {
                            const x = values.length === 1 ? 50 : (index / (values.length - 1)) * 100;
                            const y = 36 - ((value - min) / range) * 32;
                            return `,`;
                          });
                          return <polyline fill="none" stroke="currentColor" strokeWidth="1.2" points={coords.join(" ")} className="text-emerald-400" />;
                        })()}
                      </svg>
                    ) : (
                      <div className="flex h-full items-center justify-center text-xs text-zinc-600">Belum ada closed trade</div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </Section>

      </div>

      {/* ENGINE INFO */}
      <Section
        title="ENGINE STATUS"
        subtitle="Frontend hanya membaca hasil Liquidity Flow Engine"
      >
        <div className="grid gap-2 text-xs sm:grid-cols-2">
          <div className="rounded-lg bg-zinc-900 p-3">
            <span className="text-zinc-600">Strategy</span>
            <div className="mt-1 font-bold text-white">
              LIQUIDITY_FLOW
            </div>
          </div>

          <div className="rounded-lg bg-zinc-900 p-3">
            <span className="text-zinc-600">Symbol</span>
            <div className="mt-1 font-bold text-white">
              {symbol}
            </div>
          </div>

          <div className="rounded-lg bg-zinc-900 p-3">
            <span className="text-zinc-600">Direction</span>
            <div className="mt-1 font-bold text-white">
              {direction}
            </div>
          </div>

          <div className="rounded-lg bg-zinc-900 p-3">
            <span className="text-zinc-600">RR</span>
            <div className="mt-1 font-bold text-white">
              {fmt(rr)}
            </div>
          </div>
        </div>
      </Section>

    </div>
  );
}

export default LiquidityFlow;
